import { useContext } from 'react'

import { useDeepEffect, useSetState } from '@domain-group/react-hooks'

import { digObject, launchModal, sortArrayBy } from '@domain-group/javascript-utils'

import useIntegration, { useIntegrationForm } from '@hooks/useIntegration'

import PageContext from '@contexts/pageContext'

import callApi from '@functions/callApi'

import useSelector from './useSelector'

type RequestOptions = {
  service_provider: string,
}

export type IntegrationData = {
  active_session: boolean,
  id: number,
  integrable: {
    id?: number,
    type?: string,
  },
  new_version: boolean,
  refreshed: boolean,
  service: string,
  token?: string,
}

export type IntegrationAccounts = {
  name: string,
  code: string,
}

type UseInvoiceIntegrationSettings = {
  integrationData: IntegrationData | {},
  performHttpRequests?: boolean,
  requestOptions?: RequestOptions,
}

export type IntegrationOrganization = {
  name: string,
  short_code: string,
}

const defaultState = {
  integrationOrganizations: [] as IntegrationOrganization[],
  integrationRevenueAccounts: [] as IntegrationAccounts[],
  integrationExpenseAccounts: [] as IntegrationAccounts[],
  loadingOrganizations: false,
  loadingExpenseAccounts: false,
  loadingRevenueAccounts: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const loadIntegrationOrganization = async (requestOptions: RequestOptions, setState: SetState) => {
  setState({ loadingOrganizations: true })

  const result = await callApi<IntegrationData>('/accounting_providers/organisations', requestOptions)
  // @ts-expect-error TS2740
  if (result.success) setState({ integrationOrganizations: result.data })

  setState({ loadingOrganizations: false })

  return result
}

const loadIntegrationRevenueAccounts = async (requestOptions: RequestOptions, setState: SetState) => {
  setState({ loadingRevenueAccounts: true })

  const result = await callApi<IntegrationData>('/accounting_providers/accounts', {
    ...requestOptions,
    account_type: 'revenue',
  })
  // @ts-expect-error TS2740
  if (result.success) setState({ integrationRevenueAccounts: result.data })

  setState({ loadingRevenueAccounts: false })

  return result
}

const loadIntegrationExpenseAccounts = async (requestOptions: RequestOptions, setState: SetState) => {
  setState({ loadingExpenseAccounts: true })

  const result = await callApi<IntegrationData>('/accounting_providers/accounts', {
    ...requestOptions,
    account_type: 'expense',
  })
  // @ts-expect-error TS2322
  if (result.success) setState({ integrationExpenseAccounts: result.data })

  setState({ loadingExpenseAccounts: false })

  return result
}

function useInvoiceIntegrationSettings(options: UseInvoiceIntegrationSettings) {
  const { performHttpRequests, requestOptions, integrationData: initIntegrationData } = options

  const { services } = useSelector(reduxState => reduxState.entities)
  const { integrations: { updating } } = useSelector(reduxState => reduxState)

  const [state, setState] = useSetState(defaultState)
  const {
    integrationExpenseAccounts,
    integrationOrganizations,
    integrationRevenueAccounts,
    loadingExpenseAccounts,
    loadingOrganizations,
    loadingRevenueAccounts,
  } = state

  const loading = loadingOrganizations || loadingExpenseAccounts || loadingRevenueAccounts

  const { callbacks } = useContext(PageContext)

  useDeepEffect(() => {
    if (performHttpRequests){
      // @ts-expect-error TS2345
      loadIntegrationOrganization(requestOptions, setState)
      // @ts-expect-error TS2345
      loadIntegrationRevenueAccounts(requestOptions, setState)
      // @ts-expect-error TS2345
      loadIntegrationExpenseAccounts(requestOptions, setState)
    }
  }, [requestOptions, performHttpRequests])

  const integrationData = {
    ...initIntegrationData,
    access_token: digObject(initIntegrationData, 'token', ''),
    integrable_id: digObject(initIntegrationData, 'integrable.id'),
    integrable_type: digObject(initIntegrationData, 'integrable.type', ''),
    service_provider: digObject(initIntegrationData, 'service', 'xero'),
  }

  const integrationPayload = useIntegration(initIntegrationData)
  const { callbacks: { createOrUpdateIntegration }, integration } = integrationPayload

  const integrationForm = useIntegrationForm(integration)

  return {
    callbacks: {
      createOrUpdateIntegration,
      invoiceIntegrationSettings: () => launchModal({
        callbacks,
        modalKey: 'InvoiceIntegrationSettingsModal',
        payload: {
          integrationData,
          integrationExpenseAccounts,
          integrationOrganizations,
          integrationRevenueAccounts,
        },
      }),
    },
    integrationForm,
    loading,
    sortedServices: sortArrayBy(Object.values(services), 'asc', 'title'),
    state,
    updating,
  }
}

export type UseInvoiceIntegrationSettingsPayload = ReturnType<typeof useInvoiceIntegrationSettings>

export default useInvoiceIntegrationSettings
